import { Link } from "react-router-dom";
import { ArrowLeft, Cookie, Settings, BarChart3, Shield } from "lucide-react";
import { Button } from "../components/ui/button";
import Footer from "../components/Footer";

export default function CookieDeclarationPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-[#00B894] transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <img src="/images/gigseasy-logo.svg" alt="GigsEasy" className="h-10 mb-6" />
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Cookie Declaration</h1>
          <p className="text-slate-500">Last updated: March 8, 2026</p>
        </div>

        <div className="prose prose-slate max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">What Are Cookies?</h2>
            <p className="text-slate-600 mb-4">
              Cookies are small text files that are placed on your device when you visit a website. They help the website 
              remember your preferences and improve your browsing experience. GigsEasy uses cookies to provide, protect, 
              and improve our services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Types of Cookies We Use</h2>
            
            <div className="space-y-6">
              <div className="bg-slate-50 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Shield className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900">Essential Cookies</h3>
                </div>
                <p className="text-slate-600 mb-3">
                  These cookies are necessary for the website to function and cannot be disabled. They are used for:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-1">
                  <li>User authentication and session management</li>
                  <li>Security features and fraud prevention</li>
                  <li>Remembering your login status</li>
                  <li>Processing tasks and submissions</li>
                </ul>
                <p className="text-sm text-slate-500 mt-3">Duration: Session to 30 days</p>
              </div>

              <div className="bg-slate-50 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Settings className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900">Functional Cookies</h3>
                </div>
                <p className="text-slate-600 mb-3">
                  These cookies enable enhanced functionality and personalization:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-1">
                  <li>Remembering your preferences and settings</li>
                  <li>Language and region preferences</li>
                  <li>Dashboard customizations</li>
                  <li>Recently viewed tasks</li>
                </ul>
                <p className="text-sm text-slate-500 mt-3">Duration: 1 year</p>
              </div>

              <div className="bg-slate-50 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900">Analytics Cookies</h3>
                </div>
                <p className="text-slate-600 mb-3">
                  These cookies help us understand how visitors interact with our website:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-1">
                  <li>Pages visited and time spent</li>
                  <li>Features used and task completion rates</li>
                  <li>Error tracking and performance monitoring</li>
                  <li>User flow analysis</li>
                </ul>
                <p className="text-sm text-slate-500 mt-3">Duration: 2 years</p>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Third-Party Cookies</h2>
            <p className="text-slate-600 mb-4">
              We may use third-party services that set their own cookies:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li><strong>Google Analytics:</strong> Website usage analysis</li>
              <li><strong>Payment Processors:</strong> Secure payment processing (PayPal)</li>
              <li><strong>Customer Support:</strong> Live chat and support features</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Managing Cookies</h2>
            <p className="text-slate-600 mb-4">
              You can control and manage cookies in several ways:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li><strong>Browser Settings:</strong> Most browsers allow you to refuse or delete cookies through settings</li>
              <li><strong>Cookie Preferences:</strong> Use our cookie preference center (when available)</li>
              <li><strong>Opt-Out Links:</strong> Visit third-party opt-out pages for analytics cookies</li>
            </ul>
            <p className="text-slate-600 mt-4">
              Note: Disabling essential cookies may affect the functionality of our platform and prevent you from 
              logging in or completing tasks.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Cookie List</h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-slate-200 text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="border border-slate-200 px-4 py-2 text-left">Cookie Name</th>
                    <th className="border border-slate-200 px-4 py-2 text-left">Purpose</th>
                    <th className="border border-slate-200 px-4 py-2 text-left">Duration</th>
                    <th className="border border-slate-200 px-4 py-2 text-left">Type</th>
                  </tr>
                </thead>
                <tbody className="text-slate-600">
                  <tr>
                    <td className="border border-slate-200 px-4 py-2">session_id</td>
                    <td className="border border-slate-200 px-4 py-2">User session management</td>
                    <td className="border border-slate-200 px-4 py-2">Session</td>
                    <td className="border border-slate-200 px-4 py-2">Essential</td>
                  </tr>
                  <tr>
                    <td className="border border-slate-200 px-4 py-2">auth_token</td>
                    <td className="border border-slate-200 px-4 py-2">Authentication</td>
                    <td className="border border-slate-200 px-4 py-2">30 days</td>
                    <td className="border border-slate-200 px-4 py-2">Essential</td>
                  </tr>
                  <tr>
                    <td className="border border-slate-200 px-4 py-2">preferences</td>
                    <td className="border border-slate-200 px-4 py-2">User preferences</td>
                    <td className="border border-slate-200 px-4 py-2">1 year</td>
                    <td className="border border-slate-200 px-4 py-2">Functional</td>
                  </tr>
                  <tr>
                    <td className="border border-slate-200 px-4 py-2">_ga</td>
                    <td className="border border-slate-200 px-4 py-2">Google Analytics</td>
                    <td className="border border-slate-200 px-4 py-2">2 years</td>
                    <td className="border border-slate-200 px-4 py-2">Analytics</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Updates to This Declaration</h2>
            <p className="text-slate-600 mb-4">
              We may update this Cookie Declaration periodically to reflect changes in our practices or for legal reasons. 
              Please check back regularly for updates.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Contact Us</h2>
            <p className="text-slate-600 mb-4">
              If you have questions about our use of cookies, please contact us at{" "}
              <a href="mailto:privacy@gigseasy.com" className="text-[#00B894] hover:underline">privacy@gigseasy.com</a>
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
